import {Component, OnInit, Input, ViewChild, Output, EventEmitter} from '@angular/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { FormGroup, FormBuilder } from '@angular/forms';
import { FormService } from '../../../fw/services/form.service';
import { ValidationMessageComponent } from '../../shared/validation-message.component';
import { SharedValidators } from '../../shared/shared-validators';
import { AutoUnsubscribe } from '../../../fw/decoractors/AutoUnsubscribe';
import {DtoSalaryMutation} from "./models/DtoSalaryMutation";
import {DtoEmployeeBaseItems} from "./models/DtoEmployeeBaseItems";
import {SharedService} from "../../shared/shared.service";
import {EmployeeService} from "./employee.service";

@Component({
    selector: 'employee-salary',
    template: require('./employee-salary.component.html'),
    styles: [require('./employee-salary.component.css')]
})
@AutoUnsubscribe()

export class EmployeeSalaryComponent implements OnInit {
    @Input() dialog: DialogRef;
    @Input() dtoObject: DtoSalaryMutation;
    @Input() baseItems: DtoEmployeeBaseItems;
    @Input() birthDate: Date;
    @Input() currentCaoHolidayHours: number;
    @Output() salarySaved = new EventEmitter<DtoSalaryMutation>();
    @ViewChild('validationMessage', {static: false}) validationMessage: ValidationMessageComponent;
    form: FormGroup = this.fb.group({
        contractHours: [0, [SharedValidators.customNumberRequired]],
        mutationDateTime: [this.ss.getCurrentDateOnly(), [SharedValidators.customRequired]],
        caoEntityId: [null, [SharedValidators.customNumberRequired]],
        caoExperienceId: [null, [SharedValidators.customNumberRequired]],
        brutoCAOSalary: {value: 0, disabled: true},
        brutoCAOHourSalary: {value: 0, disabled: true},
        brutoHourSalaryExtra: 0,
        travelCostsUntaxed: {value: 0, disabled: true},
        extraSalaryTaxed: 0,
        extraSalaryUntaxed: 0,
        salaryEmployerTax: 0,
        extraSalaryTaxedCosts: 0,
        totalSalaryCosts: {value: 0, disabled: true},
        employerCosts: {value: 0, disabled: true},
        travelCostsTaxed: {value: 0, disabled: true}
    });
    subTotalSalaryCosts: number;
    extraSalaryTaxed: number;
    extraSalaryUntaxed: number;

    constructor(private fb: FormBuilder,
                private fs: FormService,
                private ds: EmployeeService,
                private ss: SharedService) { }

    ngOnInit() {
        this.checkForUseExperience();

        this.fs.patchDtoToForm(this.dtoObject, this.form);
        if (this.dtoObject.id === 0 || this.ss.getDateToCompare(this.dtoObject.mutationDateTime) >= this.ss.getCurrentDateToCompare())
            this.calculateSalary();

        this.form.get('caoEntityId').valueChanges.subscribe(newVal => { this.calculateSalary(); });
        this.form.get('caoExperienceId').valueChanges.subscribe(newVal => { this.calculateSalary(); });
        this.form.get('brutoHourSalaryExtra').valueChanges.subscribe(newVal => { this.calculateSalary(); });
        this.form.get('extraSalaryUntaxed').valueChanges.subscribe(newVal => { this.calculateSalary(); });
        this.form.get('extraSalaryTaxed').valueChanges.subscribe(newVal => { this.calculateSalary(); });
        this.form.get('salaryEmployerTax').valueChanges.subscribe(newVal => { this.calculateSalary(); });
        this.form.get('contractHours').valueChanges.subscribe(newVal => { this.calculateSalary(); });

        this.form.valueChanges.subscribe(() =>
            {if (this.validationMessage) this.validationMessage.getFormValidationErrors();}
        );
    }

    checkForUseExperience() {
        if (this.birthDate && this.ss.getAge(this.birthDate) < 21) {
            this.form.get('caoExperienceId').disable();
        }
    }

    calculateSalary() {
        if (this.form.get('caoEntityId').value) {
            let age = 21;
            if (this.birthDate && this.ss.getAge(this.birthDate) < 21) {
                age = this.ss.getAge(this.birthDate);
            }
            let experienceId = null;
            if (this.form.get('caoExperienceId').value)
                experienceId = this.form.get('caoExperienceId').value;
            if (age < 21)
                experienceId = null;

            let entity = this.baseItems.caoEntities.find(x => x.id == this.form.get('caoEntityId').value);
            if (entity) {
                if (entity.branch > 0 || (this.currentCaoHolidayHours && this.currentCaoHolidayHours == 0)) {
                    this.dtoObject.caoHolidays = entity.caoHolidays;
                }

                let salary = entity.caoSalaries.find(x => x.age == age && x.caoExperienceId == experienceId);
                if (salary) {
                    let salaryToUse = salary.salary;
                    if (this.baseItems.wagesType == 1) {
                        salaryToUse = salaryToUse * 0.98;
                    }
                    let caoWeek = (salaryToUse * 12) / 52;
                    let caohour = (caoWeek / entity.caoHours);
                    let caoSalary = this.ss.round(((caohour + this.form.get('brutoHourSalaryExtra').value) * this.form.get('contractHours').value * 52) / 12, 2);
                    let extraSalaryTax = this.form.get('extraSalaryTaxed').value + this.ss.round((this.form.get('extraSalaryTaxed').value / 100) * this.form.get('salaryEmployerTax').value, 2);
                    let employerTax = this.ss.round(((caoSalary + this.form.get('travelCostsUntaxed').value + this.form.get('extraSalaryTaxed').value) / 100) * this.form.get('salaryEmployerTax').value, 2);
                    let totalSalary = (caoSalary + this.form.get('travelCostsUntaxed').value + this.form.get('travelCostsUntaxed').value + this.form.get('extraSalaryTaxed').value + this.form.get('extraSalaryUntaxed').value + employerTax);

                    this.form.patchValue({ ['brutoCAOHourSalary']: caohour });
                    this.form.patchValue({ ['brutoCAOSalary']: caoSalary });
                    this.form.patchValue({ ['extraSalaryTaxedCosts']: extraSalaryTax });
                    this.form.patchValue({ ['employerCosts']: employerTax });
                    this.subTotalSalaryCosts = (caoSalary + this.form.get('travelCostsUntaxed').value + this.form.get('extraSalaryTaxed').value + employerTax);
                    this.extraSalaryTaxed = this.form.get('extraSalaryTaxed').value;
                    this.extraSalaryUntaxed = this.form.get('extraSalaryUntaxed').value;
                    this.form.patchValue({ ['totalSalaryCosts']: totalSalary });
                }
            }
        }
    }

    activeCaoEnities() {
        if (this.baseItems) {
            return this.baseItems.caoEntities.filter(x => x.inactive == false);
        }
        return null;
    }

    btnCancelClicked(event) {
        this.closeWindow(false);
    }

    btnSaveClicked(event) {
        this.validationMessage.validateForm();

        if (this.form.valid) {
            let result = this.fs.patchFormToDto(this.dtoObject, this.form);
            this.ds.saveEmployeeSalaryMutation(result).subscribe(res => {
                result.id = res.id;
                this.salarySaved.emit(result);
                this.closeWindow(true);
            });
        }
    }

    closeWindow(result: boolean) {
        if (this.dialog) {
            this.dialog.dialog.instance.action.emit(result);
            this.dialog.close();
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Scripts/app/employees/employee-details/employee-salary.component.ts